// Settings -> Configuration (documentation/configuration.md): platform-administration area with
// one sub-section per integration. Data comes only from the RadioOS backend.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  ConfigurationError, getAudit, getDeployment, getEntra, getHetzner, getLiveKit, getOverview, getSimply, saveSettings, testEntra, testLiveKit,
  validateSettings, type AuditEntry, type ConfigSection, type DeploymentSettings, type DeploymentView, type EntraView, type HetznerSettings,
  type HetznerView, type LiveKitView, type SimplySettings, type SimplyView,
} from '../../../services/configuration';
import { AuditTable, ConfigurationNav, DeploymentPanel, EntraPanel, HetznerPanel, LiveKitPanel, SimplyPanel } from './ConfigurationViews';
import type { ConfigurationSubsection } from './configurationSections';
import IdentityConfiguration from './IdentityConfiguration';
import { OnencePanel } from './OnenceConfiguration';
import { SessionsPanel } from './SessionsConfiguration';
import {
  getOnence, onenceTestText, removeOnenceCredentials, saveOnenceCredentials, setOnenceApiVersion, testOnence, type OnenceView,
} from '../../../services/onence';

export default function ConfigurationArea({ subsection, onNavigate, runtime }: {
  subsection: ConfigurationSubsection;
  onNavigate: (s: ConfigurationSubsection) => void;
  /** The existing runtime-services editor (server, database, Redis, LiveKit overrides). */
  runtime: ReactNode;
}) {
  const [statuses, setStatuses] = useState<Partial<Record<ConfigSection, string>>>({});
  const [entra, setEntra] = useState<EntraView | null>(null);
  const [livekit, setLiveKit] = useState<LiveKitView | null>(null);
  const [hetzner, setHetzner] = useState<HetznerView | null>(null);
  const [simply, setSimply] = useState<SimplyView | null>(null);
  const [deployment, setDeployment] = useState<DeploymentView | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [onence, setOnence] = useState<OnenceView | null>(null);
  const [hetznerForm, setHetznerForm] = useState<HetznerSettings | null>(null);
  const [simplyForm, setSimplyForm] = useState<SimplySettings | null>(null);
  const [deploymentForm, setDeploymentForm] = useState<DeploymentSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback(async () => {
    try {
      getOverview().then((o) => setStatuses(o.sections)).catch(() => undefined);
      switch (subsection) {
        case 'entra': setEntra(await getEntra()); break;
        case 'livekit': setLiveKit(await getLiveKit()); break;
        case 'hetzner': { const v = await getHetzner(); setHetzner(v); setHetznerForm(v.desired); break; }
        case 'simply': { const v = await getSimply(); setSimply(v); setSimplyForm(v.desired); break; }
        case 'deployment': { const v = await getDeployment(); setDeployment(v); setDeploymentForm(v.target.desired); break; }
        case 'audit': setAudit(await getAudit()); break;
        case 'onence': setOnence(await getOnence()); break;
      }
    } catch (err) {
      setMessage({ ok: false, text: err instanceof Error ? err.message : String(err) });
    }
  }, [subsection]);

  useEffect(() => {
    setMessage(null);
    load();
  }, [load]);

  const run = async (action: () => Promise<string>) => {
    setBusy(true);
    setMessage(null);
    try {
      setMessage({ ok: true, text: await action() });
    } catch (err) {
      const issues = err instanceof ConfigurationError && err.issues.length ? `: ${err.issues.filter((i) => i.severity === 'error').map((i) => i.message).join('; ')}` : '';
      setMessage({ ok: false, text: (err instanceof Error ? err.message : String(err)) + issues });
    } finally {
      setBusy(false);
      await load();
    }
  };

  const save = (owner: 'hetzner' | 'simply' | 'deployment', settings: HetznerSettings | SimplySettings | DeploymentSettings | null) => () =>
    settings && run(async () => {
      const r = await saveSettings(owner, settings);
      return r.changed_fields.length ? `Saved: ${r.changed_fields.map((c) => c.field).join(', ')}` : 'Saved (no changes)';
    });
  const validate = (owner: 'hetzner' | 'simply' | 'deployment') => () =>
    run(async () => {
      const r = await validateSettings(owner);
      return r.valid ? (r.complete ? 'Valid and complete' : `Valid but incomplete (${r.issues.length} field(s) missing)`) : `Invalid: ${r.issues.filter((i) => i.severity === 'error').map((i) => i.message).join('; ')}`;
    });

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-2xl font-bold">Configuration</h2>
        <p className="text-sm text-gray-400">Platform configuration and infrastructure status. Secrets are never displayed; provisioning credentials are not held by RadioOS.</p>
      </div>
      <ConfigurationNav active={subsection} statuses={statuses} onSelect={onNavigate} />
      {message && (
        <div role="status" className={`mb-6 rounded-lg p-3 text-sm border ${message.ok ? 'bg-green-900/30 border-green-700 text-green-200' : 'bg-red-900/30 border-red-700 text-red-200'}`}>{message.text}</div>
      )}
      {subsection === 'runtime' && runtime}
      {subsection === 'entra' && entra && (
        <EntraPanel view={entra} busy={busy} onTest={() => run(async () => {
          const r = await testEntra();
          return r.success ? 'Microsoft Entra: discovery and client secret verified' : `Microsoft Entra check failed: discovery ${r.result.discovery}, credential ${r.result.credential}${r.result.error_code ? ` (${r.result.error_code})` : ''}`;
        })} />
      )}
      {subsection === 'livekit' && livekit && (
        <LiveKitPanel view={livekit} busy={busy} onTest={() => run(async () => {
          const r = await testLiveKit();
          if (!r.success) throw new Error(`LiveKit ${r.detail}`);
          return `LiveKit ${r.detail}`;
        })} />
      )}
      {subsection === 'hetzner' && hetzner && hetznerForm && (
        <HetznerPanel view={hetzner} form={hetznerForm} onChange={setHetznerForm} busy={busy} onSave={save('hetzner', hetznerForm)} onValidate={validate('hetzner')} />
      )}
      {subsection === 'simply' && simply && simplyForm && (
        <SimplyPanel view={simply} form={simplyForm} onChange={setSimplyForm} busy={busy} onSave={save('simply', simplyForm)} onValidate={validate('simply')} />
      )}
      {subsection === 'deployment' && deployment && deploymentForm && (
        <DeploymentPanel view={deployment} form={deploymentForm} onChange={setDeploymentForm} busy={busy} onSave={save('deployment', deploymentForm)} onValidate={validate('deployment')} />
      )}
      {subsection === 'onence' && onence && (
        <OnencePanel view={onence} busy={busy}
          onSave={(clientId, clientSecret) => new Promise((resolve, reject) => {
            run(async () => {
              try {
                await saveOnenceCredentials(clientId, clientSecret);
              } catch (err) {
                reject(err);
                throw err;
              }
              resolve(undefined);
              return '1NCE API credential stored (write-only). Use Test connection to verify it.';
            });
          })}
          onTest={() => run(async () => {
            const r = await testOnence();
            if (!r.success) throw new Error(onenceTestText(r));
            return onenceTestText(r);
          })}
          onSetVersion={(v) => {
            const note = v === 'v1' ? ' v1 is temporary compatibility for legacy SIMs; 1NCE retires it at the end of 2026.' : '';
            if (window.confirm(`Switch every 1NCE SIM lookup to Management API ${v}?${note}`)) {
              run(async () => { await setOnenceApiVersion(v); return `1NCE Management API ${v} is now active. Run Test connection to verify SIM API access.`; });
            }
          }}
          onRemove={() => {
            if (window.confirm('Remove the stored 1NCE API credential? SIM lookups stop until a new credential is set.')) {
              run(async () => { await removeOnenceCredentials(); return '1NCE API credential removed'; });
            }
          }} />
      )}
      {subsection === 'audit' && <AuditTable entries={audit} />}
      {subsection === 'sessions' && <SessionsPanel />}
      {(subsection === 'auth-providers' || subsection === 'mfa-policy' || subsection === 'delegation' || subsection === 'mail') && (
        <IdentityConfiguration section={subsection} />
      )}
    </div>
  );
}
